
import React, { useState, useEffect } from 'react';
import { 
    Sparkles, Loader2, BookOpen, Send, FileJson, 
    Trash2, Edit, Plus, Info, ListChecks, 
    ArrowDownAZ, Wand2, Settings, AlertCircle, 
    LayoutGrid, ChevronRight, CheckCircle2, 
    GripVertical, Hash, Save, X, Target, Gauge, 
    Fingerprint, Cpu, Database, Zap, AlertTriangle, CheckCircle,
    CheckSquare, ListOrdered, MousePointer2, Award, ShieldAlert,
    FileWarning, ShieldCheck, Stethoscope, Microscope
} from 'lucide-react';
// Fixed: Added missing import for Type from @google/genai
import { Type } from "@google/genai";
import { BackendQuestion, QuestionDifficulty, CertificationType } from '../../types';
import { PromptService } from '../../services/promptService';
import { runAiTask, randomizeQuestionOptions } from '../../services/geminiService';
import { StorageService } from '../../services/storageService';
import { CNA_DOMAINS, US_STATES } from '../../data/mockData';

interface ValidatedQuestion extends BackendQuestion {
    validationErrors?: string[];
    isValid?: boolean;
}

export const AdminAIFactory: React.FC = () => {
  const [certId, setCertId] = useState('cna');
  const [certs, setCerts] = useState<CertificationType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sourceText, setSourceText] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCommitting, setIsCommitting] = useState(false);
  const [generatedQuestions, setGeneratedQuestions] = useState<ValidatedQuestion[]>([]);
  const [selectedState, setSelectedState] = useState('Global');
  const [status, setStatus] = useState<{type: 'error' | 'success' | 'warning', text: string} | null>(null);
  
  const [params, setParams] = useState({
    count: 5,
    difficulty: 'Medium' as QuestionDifficulty,
    category: 'Basic Nursing Skills',
    preferredTypes: ['multiple-choice'],
    optionsPerQuestion: 4,
    correctPerQuestion: 1,
    isScenarioMode: true
  });

  useEffect(() => {
    setIsLoading(true);
    StorageService.getCertifications().then(data => {
        setCerts(data);
        setIsLoading(false);
    }).catch(() => setIsLoading(false));
  }, []);

  const activeCert = certs.find(c => c.id === certId);
  const activeCategories = activeCert?.categories?.length ? activeCert.categories : CNA_DOMAINS;

  // Sync AI generation category when certId changes
  useEffect(() => {
    if (activeCategories.length > 0) {
        setParams(prev => ({
            ...prev,
            category: activeCategories[0]
        }));
    }
  }, [certId, certs]);

  const validateQuestion = (q: any): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    // Check Options Count
    if (!q.options || q.options.length !== params.optionsPerQuestion) {
        errors.push(`Expected ${params.optionsPerQuestion} options, found ${q.options?.length || 0}.`);
    }

    // Check Correct Answers Count
    const correctCount = q.options?.filter((o: any) => o.isCorrect).length || 0;
    if (correctCount !== params.correctPerQuestion) {
        errors.push(`Expected ${params.correctPerQuestion} correct answer(s), found ${correctCount}.`);
    }

    // Scenario Depth Check
    if (params.isScenarioMode && q.text && q.text.length < 150) {
        errors.push("Scenario narrative is too brief for high-fidelity practice.");
    }

    // Basic Integrity
    if (!q.text || q.text.trim().length < 10) errors.push("Narrative too short or empty.");
    if (!q.explanation || q.explanation.trim().length < 10) errors.push("Explanation insufficient.");

    return {
        isValid: errors.length === 0,
        errors
    };
  };

  const generateWithAI = async () => {
    if (!sourceText.trim()) return;
    setIsGenerating(true);
    setStatus(null);

    const prompts = PromptService.getPrompts();

    const scenarioInstruction = params.isScenarioMode 
        ? `GENERATE HIGH-FIDELITY CLINICAL SCENARIOS for the "${params.category}" domain. 
           Each item MUST be a narrative-driven case study involving a Nurse Aide and a Resident. 
           STRUCTURE: Start with a situational setup (1-2 paragraphs), followed by a specific clinical observation or complication, then ask "What is the BEST action the nurse aide should take?".
           TONE: Professional, realistic, and mapped to state board safety protocols.
           GOAL: Test critical thinking, safety prioritisation, and resident-centered care logic.`
        : `Generate standard ${params.difficulty} level questions for: "${params.category}".`;

    // Support generating >15 items via concurrent batching (up to 50 items)
    const totalRequested = Math.max(1, Math.min(Number(params.count) || 5, 50));
    const BATCH_SIZE = 15;
    const batchSizes: number[] = [];
    let remaining = totalRequested;
    while (remaining > 0) {
        const chunk = Math.min(remaining, BATCH_SIZE);
        batchSizes.push(chunk);
        remaining -= chunk;
    }

    try {
      const generateBatch = async (batchCount: number, bIdx: number) => {
        const response = await runAiTask(
          `${scenarioInstruction}
              TARGET QUANTITY: ${batchCount}
              KNOWLEDGE BASE: "${sourceText}"
              STRICT FORMATTING CONSTRAINTS:
              1. Every question MUST have exactly ${params.optionsPerQuestion} options.
              2. Every question MUST have exactly ${params.correctPerQuestion} option(s) marked as isCorrect: true.
              3. OPTION ORDER & RANDOMIZATION: The position of the correct answer MUST vary across positions A, B, C, and D. Do NOT always place the correct answer as Option A.
              4. Use the category name: "${params.isScenarioMode ? 'Clinical Scenarios' : params.category}"`,
          prompts.factory,
          'gemini-3.1-pro-preview',
          'application/json',
          {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                text: { type: Type.STRING, description: params.isScenarioMode ? "The full case narrative and question" : "The question stem" },
                type: { type: Type.STRING, enum: ['multiple-choice', 'ordering', 'charting', 'observation', 'fill-in-the-blank'] },
                explanation: { type: Type.STRING, description: "Deep clinical rationale explaining why the chosen answer is the BEST action according to board standards." },
                options: {
                  type: Type.ARRAY,
                  items: {
                    type: Type.OBJECT,
                    properties: {
                      text: { type: Type.STRING },
                      isCorrect: { type: Type.BOOLEAN }
                    },
                    required: ["text", "isCorrect"]
                  }
                }
              },
              required: ["text", "type", "options", "explanation"]
            }
          }
        );

        try {
          return JSON.parse(response.text || '[]');
        } catch (err) {
          console.warn(`Failed to parse batch ${bIdx + 1}:`, err);
          return [];
        }
      };

      const batchResults = await Promise.all(
        batchSizes.map((size, idx) => generateBatch(size, idx))
      );

      const parsed = batchResults.flat();
      if (parsed.length === 0) {
        throw new Error("No questions were returned by the AI synthesis engine.");
      }

      const finalized = parsed.map((q: any, idx: number) => {
        const targetSlot = (idx + Math.floor(Math.random() * 4)) % (q.options?.length || params.optionsPerQuestion || 4);
        const randomizedOptions = randomizeQuestionOptions(q.options || [], targetSlot);
        const validation = validateQuestion({ ...q, options: randomizedOptions });

        return {
            ...q,
            _id: `gen_${Date.now()}_${idx}_${Math.random().toString(36).substr(2, 5)}`,
            options: randomizedOptions.map((o: any, oIdx: number) => ({
                ...o,
                _id: `gen_opt_${Date.now()}_${idx}_${oIdx}`
            })),
            category: params.isScenarioMode ? 'Clinical Scenarios' : params.category, 
            difficulty: params.difficulty,
            vendorTags: ['Global', 'Scenario'],
            stateSpecificTags: selectedState === 'Global' ? [] : [selectedState],
            isValid: validation.isValid,
            validationErrors: validation.errors
        };
      });
      
      const validCount = finalized.filter((q: any) => q.isValid).length;
      setGeneratedQuestions(prev => [...finalized, ...prev]);
      
      if (validCount < finalized.length) {
          setStatus({ 
              type: 'warning', 
              text: `Synthesized ${finalized.length} items with randomized answers, but ${finalized.length - validCount} failed integrity checks.` 
          });
      } else {
          setStatus({ type: 'success', text: `Engine synthesized ${finalized.length} verified items with randomized answer positions across A-D.` });
      }
    } catch (error: any) {
      console.error("AI Generation failed:", error);
      setStatus({ type: 'error', text: 'Creation failed: ' + (error?.message || 'Ensure API access is active.') });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCommitAll = async () => {
    const validOnly = generatedQuestions.filter(q => q.isValid);
    if (validOnly.length === 0) return;
    
    setIsCommitting(true);
    setStatus(null);
    try {
        await StorageService.saveQuestionsBulk(certId, validOnly);
        setStatus({ type: 'success', text: `Registry updated with ${validOnly.length} verified items.` });
        setGeneratedQuestions([]);
        setSourceText('');
        setIsModalOpen(false);
    } catch (e) {
        setStatus({ type: 'error', text: 'Commit failed.' });
    } finally {
        setIsCommitting(false);
    }
  };

  if (isLoading) {
      return (
          <div className="flex flex-col items-center justify-center py-40 animate-fade-in opacity-40">
              <Loader2 className="w-12 h-12 text-medical-600 animate-spin mb-6" />
              <p className="text-[10px] font-black text-slate-400 tracking-[0.4em]">Initializing AI Synthesis Edge...</p>
          </div>
      );
  }

  const totalInvalid = generatedQuestions.filter(q => !q.isValid).length;

  return (
    <div className="space-y-8 animate-fade-in pb-12">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-6 text-slate-900">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="px-3 py-1 bg-medical-50 text-medical-600 text-[10px] font-black rounded-full border border-medical-100">Content Engine</span>
          </div>
          <h2 className="text-3xl font-black tracking-tight flex items-center gap-3">
            <Sparkles className="text-medical-500" /> AI Studio
          </h2>
          <p className="text-slate-500 font-medium mt-1">High-fidelity question synthesis including complex Clinical Scenarios.</p>
        </div>
        
        <div className="flex gap-3">
            <div className="bg-white p-1.5 rounded-2xl border border-slate-200 shadow-sm flex gap-1">
                {certs.map(c => (
                    <button key={c.id} onClick={() => setCertId(c.id)} className={`px-5 py-2.5 rounded-xl text-[10px] font-black transition-all ${certId === c.id ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-400 hover:text-slate-600'}`}>{c.shortName}</button>
                ))}
            </div>
            <button 
                onClick={() => { setIsModalOpen(true); setStatus(null); }}
                className="px-8 py-4 bg-indigo-600 text-white rounded-[1.5rem] font-black text-[10px] tracking-[0.2em] flex items-center gap-3 shadow-2xl hover:bg-indigo-50 transition-all group border-b-4 border-indigo-800"
            >
                <Plus size={18} /> Launch Builder
            </button>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-[150] bg-slate-900/80 backdrop-blur-md flex items-start md:items-start justify-center p-4 pt-10 md:pt-16 overflow-y-auto custom-scrollbar">
            <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-7xl h-auto max-h-none flex flex-col animate-pop overflow-hidden text-slate-900 relative my-auto">
                <div className="px-8 py-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 shrink-0">
                    <div>
                        <h3 className="text-2xl font-black tracking-tight">AI Item Builder</h3>
                        <p className="text-[10px] font-black text-slate-400 mt-1">Targeting {certs.find(c => c.id === certId)?.name} Registry</p>
                    </div>
                    <button onClick={() => setIsModalOpen(false)} className="p-3 hover:bg-slate-100 rounded-2xl text-slate-400"><X size={24} /></button>
                </div>

                <div className="flex-1 flex flex-col lg:flex-row overflow-hidden min-h-0">
                    <div className="w-full lg:w-5/12 p-8 border-r border-slate-50 overflow-y-auto custom-scrollbar space-y-8 bg-slate-50/20 shrink-0">
                        
                        <div className="space-y-3">
                            <label className="text-[10px] font-black text-slate-400 tracking-widest flex items-center gap-2"><LayoutGrid size={12}/> Synthesis Mode</label>
                            <div className="grid grid-cols-2 gap-2">
                                <button 
                                    onClick={() => setParams({...params, isScenarioMode: false})}
                                    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${!params.isScenarioMode ? 'bg-medical-500 border-medical-600 text-white shadow-md' : 'bg-white border-slate-100 text-slate-400 hover:border-slate-200'}`}
                                >
                                    <CheckSquare size={16} />
                                    <span className="text-[9px] font-black tracking-widest">Standard MCQ</span>
                                </button>
                                <button 
                                    onClick={() => setParams({...params, isScenarioMode: true})}
                                    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${params.isScenarioMode ? 'bg-indigo-600 border-indigo-700 text-white shadow-md' : 'bg-white border-slate-100 text-slate-400 hover:border-slate-200'}`}
                                >
                                    <Stethoscope size={16} />
                                    <span className="text-[9px] font-black tracking-widest">Clinical Scenario</span>
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 tracking-widest flex items-center gap-2"><BookOpen size={12}/> Context Domain</label>
                                <select 
                                    value={params.category}
                                    onChange={e => setParams({...params, category: e.target.value})}
                                    className="w-full p-4 bg-white border border-slate-200 rounded-2xl outline-none font-bold text-[11px] shadow-sm focus:border-medical-500 transition-colors"
                                >
                                    {activeCategories.map(d => <option key={d} value={d}>{d}</option>)}
                                </select>
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 tracking-widest flex items-center gap-2"><Gauge size={12}/> Rigor Level</label>
                                <select 
                                    value={params.difficulty}
                                    onChange={e => setParams({...params, difficulty: e.target.value as any})}
                                    className="w-full p-4 bg-white border border-slate-200 rounded-2xl font-bold text-[11px] shadow-sm focus:border-medical-500 transition-colors"
                                >
                                    <option value="Easy">Easy</option>
                                    <option value="Medium">Medium</option>
                                    <option value="Hard">Hard</option>
                                </select>
                            </div>
                        </div>

                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <label className="text-[10px] font-black text-slate-400 tracking-widest flex items-center gap-2">
                                    <Hash size={12} className="text-indigo-600" /> Synthesis Depth
                                </label>
                                <span className="text-[9px] font-bold text-slate-400">Up to 50 items</span>
                            </div>
                            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm space-y-3">
                                <div className="flex justify-between items-center px-1">
                                    <span className="text-[10px] font-black text-slate-500">Items to Generate</span>
                                    <div className="flex items-center gap-1.5">
                                        <input 
                                            type="number" 
                                            min="1" 
                                            max="50"
                                            value={params.count}
                                            onChange={e => {
                                                const val = parseInt(e.target.value);
                                                if (!isNaN(val)) setParams({...params, count: Math.max(1, Math.min(50, val))});
                                            }}
                                            className="w-14 px-2 py-1 bg-slate-50 border border-slate-200 rounded-lg text-right font-black text-sm text-indigo-600 focus:outline-none focus:border-indigo-500"
                                        />
                                        <span className="text-[11px] font-bold text-slate-400">items</span>
                                    </div>
                                </div>
                                <input 
                                    type="range" min="1" max="50" step="1"
                                    value={params.count}
                                    onChange={e => setParams({...params, count: parseInt(e.target.value)})}
                                    className="w-full h-1.5 bg-slate-100 rounded-full appearance-none cursor-pointer accent-indigo-600"
                                />
                                <div className="flex flex-wrap gap-1.5 pt-1">
                                    {[5, 10, 15, 20, 25, 30, 50].map((preset) => (
                                        <button
                                            key={preset}
                                            type="button"
                                            onClick={() => setParams({...params, count: preset})}
                                            className={`px-2.5 py-1 rounded-lg text-[10px] font-black transition-all ${
                                                params.count === preset
                                                    ? 'bg-indigo-600 text-white shadow-sm'
                                                    : 'bg-slate-50 text-slate-500 hover:bg-slate-100 hover:text-slate-800 border border-slate-100'
                                            }`}
                                        >
                                            {preset}
                                        </button>
                                    ))}
                                </div>
                                {params.count > 15 && (
                                    <p className="text-[9px] font-medium text-indigo-700 bg-indigo-50/80 px-2.5 py-1.5 rounded-lg border border-indigo-100 leading-snug">
                                        Multi-stream batching: {params.count} questions will be synthesized across {Math.ceil(params.count / 15)} concurrent AI pipelines with randomized answer order (A-D).
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Options Count</label>
                                <input 
                                    type="number" min="2" max="6"
                                    value={params.optionsPerQuestion}
                                    onChange={e => setParams({...params, optionsPerQuestion: parseInt(e.target.value)})}
                                    className="w-full p-4 bg-white border border-slate-200 rounded-2xl outline-none font-black text-sm text-indigo-600 shadow-inner"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Correct Answers</label>
                                <input 
                                    type="number" min="1" max={params.optionsPerQuestion - 1}
                                    value={params.correctPerQuestion}
                                    onChange={e => setParams({...params, correctPerQuestion: parseInt(e.target.value)})}
                                    className="w-full p-4 bg-white border border-slate-200 rounded-2xl outline-none font-black text-sm text-emerald-600 shadow-inner"
                                />
                            </div>
                        </div>

                        <div className="space-y-3">
                            <label className="text-[10px] font-black text-slate-400 tracking-widest flex items-center gap-2"><Zap size={12}/> Scenario Context / Notes</label>
                            <textarea 
                                value={sourceText}
                                onChange={e => setSourceText(e.target.value)}
                                placeholder={params.isScenarioMode ? "Paste specific clinical protocols or patient situations to turn into case studies..." : "Paste clinical protocols or study notes..."}
                                className="w-full p-8 bg-white border border-slate-200 rounded-[2rem] outline-none font-medium text-slate-800 h-64 resize-none shadow-inner focus:border-medical-500 transition-colors"
                            />
                        </div>

                        <button 
                            onClick={generateWithAI}
                            disabled={isGenerating || !sourceText.trim()}
                            className="w-full py-6 bg-slate-900 text-white rounded-[1.5rem] font-black text-xl shadow-2xl hover:bg-slate-800 transition-all flex items-center justify-center gap-4 disabled:opacity-50 border-b-4 border-black"
                        >
                            {isGenerating ? <Loader2 className="animate-spin" size={24} /> : <Wand2 size={24} />}
                            {isGenerating ? 'Synthesizing...' : params.isScenarioMode ? 'Build Scenarios' : 'Generate Batch'}
                        </button>
                    </div>

                    <div className="w-full lg:w-7/12 bg-slate-50 p-8 overflow-y-auto custom-scrollbar flex flex-col gap-6 min-h-[400px] lg:min-h-0">
                        <div className="flex items-center justify-between px-2">
                            <h4 className="text-[10px] font-black text-slate-400 tracking-widest">Staged Items ({generatedQuestions.length})</h4>
                            {totalInvalid > 0 && (
                                <button onClick={() => setGeneratedQuestions(prev => prev.filter(q => q.isValid))} className="text-[10px] font-black text-rose-600 flex items-center gap-2 hover:underline">
                                    <Trash2 size={12}/> Purge {totalInvalid} Invalid Items
                                </button>
                            )}
                        </div>

                        {generatedQuestions.length === 0 ? (
                            <div className="flex-1 flex flex-col items-center justify-center text-center opacity-20 p-12 min-h-[300px]">
                                {params.isScenarioMode ? <Stethoscope size={64} className="mb-4" /> : <Sparkles size={64} className="mb-4" />}
                                <p className="font-black text-sm">Awaiting Logic Run</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {generatedQuestions.map((q, idx) => (
                                    <div key={idx} className={`p-6 rounded-[2.5rem] shadow-sm border-2 transition-all animate-pop space-y-4 ${q.isValid ? 'bg-white border-white' : 'bg-rose-50 border-rose-100 shadow-rose-100/50'}`}>
                                        <div className="flex justify-between items-start">
                                            <div className="flex items-center gap-3">
                                                <div className={`p-2 rounded-xl text-white ${q.isValid ? 'bg-indigo-600' : 'bg-rose-600'}`}>
                                                    {q.isValid ? <ShieldCheck size={16}/> : <ShieldAlert size={16}/>}
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-[8px] font-black text-slate-400">Phase Result:</span>
                                                        <span className={`text-[8px] font-black ${q.isValid ? 'text-emerald-600' : 'text-rose-600'}`}>
                                                            {q.isValid ? 'Registry Verified' : 'Integrity Failure'}
                                                        </span>
                                                    </div>
                                                    <p className="text-[8px] font-bold text-slate-400 tracking-widest">{q.category} • {q.difficulty}</p>
                                                </div>
                                            </div>
                                            <button onClick={() => setGeneratedQuestions(prev => prev.filter((_, i) => i !== idx))} className="p-2 text-slate-300 hover:text-rose-500 transition-colors"><Trash2 size={16}/></button>
                                        </div>

                                        {!q.isValid && q.validationErrors && (
                                            <div className="p-4 bg-rose-100 rounded-2xl space-y-1">
                                                {q.validationErrors.map((err, i) => (
                                                    <div key={i} className="flex items-center gap-2 text-[10px] font-black text-rose-700">
                                                        <FileWarning size={12}/> {err}
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        <div className={`p-5 rounded-2xl border ${q.category === 'Clinical Scenarios' ? 'bg-indigo-50/50 border-indigo-100' : 'bg-slate-50 border-slate-100'}`}>
                                            <h5 className="font-bold text-slate-800 text-sm leading-relaxed whitespace-pre-wrap">"{q.text}"</h5>
                                        </div>
                                        
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                            {q.options.map((o, oIdx) => (
                                                <div key={oIdx} className={`p-3 rounded-xl text-[11px] flex items-center gap-2.5 border transition-all ${o.isCorrect ? 'bg-emerald-50 text-emerald-950 font-bold border-emerald-300 ring-2 ring-emerald-500/10' : 'bg-slate-50 text-slate-600 border-slate-100'}`}>
                                                    <div className={`w-6 h-6 rounded-lg flex items-center justify-center font-black text-[10px] shrink-0 ${o.isCorrect ? 'bg-emerald-600 text-white' : 'bg-white text-slate-400 border border-slate-200'}`}>
                                                        {String.fromCharCode(65 + oIdx)}
                                                    </div>
                                                    <span className="truncate flex-1">{o.text}</span>
                                                    {o.isCorrect && (
                                                        <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-800 rounded text-[8px] font-black uppercase tracking-wider shrink-0">
                                                            Correct
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>

                                        <div className="pt-4 border-t border-slate-50">
                                            <p className="text-[9px] font-bold text-slate-500 flex gap-2"><Info size={12} className="shrink-0 text-indigo-50" /> RATIONALE: {q.explanation}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="p-8 border-t border-slate-100 bg-white flex flex-col md:flex-row justify-between items-center gap-6 shrink-0">
                    <div className="flex items-center gap-6">
                        <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-xl border border-slate-100">
                            <ShieldCheck size={16} className="text-emerald-500" />
                            <span className="text-[10px] font-black text-emerald-700">{generatedQuestions.filter(q => q.isValid).length} Valid</span>
                        </div>
                        <div className="flex items-center gap-2 px-4 py-2 bg-rose-50 rounded-xl border border-rose-100">
                            <AlertTriangle size={16} className="text-rose-500" />
                            <span className="text-[10px] font-black text-rose-700">{totalInvalid} Rejected</span>
                        </div>
                    </div>
                    <div className="flex gap-4 w-full md:w-auto">
                        <button onClick={() => setGeneratedQuestions([])} className="flex-1 md:flex-none px-8 py-4 font-black text-[10px] text-slate-400">Purge Staging</button>
                        <button 
                            onClick={handleCommitAll} 
                            disabled={generatedQuestions.filter(q => q.isValid).length === 0 || isCommitting}
                            className="flex-1 md:flex-none px-12 py-4 bg-slate-900 text-white rounded-2xl font-black text-xs tracking-widest shadow-xl disabled:opacity-30 border-b-4 border-black active:scale-95 transition-all flex items-center justify-center gap-3"
                        >
                            {isCommitting ? <Loader2 size={18} className="animate-spin" /> : <Database size={18} />} 
                            Commit {generatedQuestions.filter(q => q.isValid).length} verified items
                        </button>
                    </div>
                </div>
            </div>
        </div>
      )}

      <div className="bg-white rounded-[2.5rem] p-10 border-4 border-dashed border-slate-100 flex flex-col items-center justify-center text-center gap-6 min-h-[400px] opacity-60">
          <Database size={64} className="text-slate-200" />
          <div>
            <h3 className="text-xl font-black text-slate-900">Logic Engine Ready</h3>
            <p className="text-slate-400 text-sm mt-1">Configure your clinical parameters and launch the builder to start high-fidelity synthesis.</p>
          </div>
      </div>
    </div>
  );
};
